import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { userApi, authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ActivationUrlCard } from '@/components/user/ActivationUrlCard';
import { Checkbox } from '@/components/ui/checkbox';
import type { InternalRole } from '@pillar-portal/database/dist/frontend';
import { Role } from '@pillar-portal/database/dist/frontend';
import { InternalRoleSchema } from '@pillar-portal/api-types';
import { BackButton } from '@/components/ui/back-button';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';

function CreateInternalUserContent() {
    const log = createComponentLogger('CreateInternalUserPage');
    const queryClient = useQueryClient();
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [role, setRole] = useState<InternalRole | undefined>(undefined);
    const [invitationToken, setInvitationToken] = useState<string | null>(null);
    const [createdUserUuid, setCreatedUserUuid] = useState<string | null>(null);
    const [skipVerification, setSkipVerification] = useState<boolean>(false);
    const activeRoleAssignmentQuery = useQuery({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });
    const requesterIsSuperadmin: boolean =
        activeRoleAssignmentQuery.data?.role === Role.SUPERADMIN;
    // Use build-time environment variable (baked in at Docker build time)
    // VITE_ prefix makes it available to client components

    const createMutation = useMutation({
        mutationFn: userApi.provisionInternalUser,
        onSuccess: (data) => {
            setInvitationToken(data.invitation_token);
            setCreatedUserUuid(data.user?.user_uuid ?? null);
            if (data.invitation_token === null && data.user !== null) {
                breadcrumbNavigate({
                    source: 'terminal',
                    target: {
                        url: `/users/${data.user.user_uuid}`,
                        title: 'User Details',
                    },
                });
            }
            runInBackground(queryClient.invalidateQueries({ queryKey: ['iusers'] }), (err) => {
                log.error('Error invalidating queries:', err);
            });
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (role === undefined) {
            throw new Error('Role is required');
        }
        createMutation.mutate({
            primary_email: email,
            emails: [email],
            role: role,
            organization_id: null,
            // Internal users don't need an organization_id
            first_name: firstName,
            last_name: lastName,
            skip_verification: requesterIsSuperadmin ? skipVerification : undefined,
        });
    };

    const resetForm = () => {
        setInvitationToken(null);
        setEmail('');
        setRole(undefined);
        setFirstName('');
        setLastName('');
        setCreatedUserUuid(null);
        runInBackground(queryClient.invalidateQueries({ queryKey: ['iusers'] }), (err) => {
            log.error('Error invalidating queries:', err);
        });
    };
    if (invitationToken !== null) {
        return (
            <div>
                <h1 className="text-3xl font-bold mb-8">Internal User Created Successfully</h1>
                <ActivationUrlCard
                    invitationToken={invitationToken}
                    userUuid={createdUserUuid ?? undefined}
                    email={email}
                    userRoles={role ? [role] : undefined}
                    emailSent={true}
                    title="Activation URL"
                    description="The user record will be created after invitation token consumption. Anchorbase Portal sends the invitation email automatically."
                    actionButtons={
                        <>
                            <BackButton
                                href="/internal-users"
                                tooltip="Back to Internal Users"
                                onClick={resetForm}
                            />
                            <Button variant="outline" onClick={resetForm}>
                                Create Another User
                            </Button>
                        </>
                    }
                />
            </div>
        );
    }

    return (
        <div>
            <BackButton
                href="/internal-users"
                tooltip="Back to Internal Users"
                onClick={() => {
                    runInBackground(
                        queryClient.invalidateQueries({ queryKey: ['iusers'] }),
                        (err) => {
                            log.error('Error invalidating queries:', err);
                        }
                    );
                }}
            />
            <div className="flex items-center gap-4 mb-6">
                <h1 className="text-3xl font-bold">Create Internal User</h1>
            </div>

            <Card className="max-w-2xl">
                <CardHeader>
                    <CardTitle>Internal User Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">First Name</Label>
                            <Input
                                id="firstName"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input
                                id="lastName"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="user@example.com"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="role">Role</Label>
                            <Select
                                value={role ?? ''}
                                onValueChange={(value) => setRole(InternalRoleSchema.parse(value))}
                                required
                            >
                                <SelectTrigger id="role">
                                    <SelectValue placeholder="Select a role" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="SUPERADMIN">Superadmin</SelectItem>
                                    <SelectItem value="INTERNAL_ADMIN">Internal Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {requesterIsSuperadmin && (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="skipVerification"
                                    checked={skipVerification}
                                    onCheckedChange={(checked) =>
                                        setSkipVerification(checked === true)
                                    }
                                />
                                <Label htmlFor="skipVerification">
                                    Skip verification (superadmin)
                                </Label>
                            </div>
                        )}

                        {createMutation.error && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {createMutation.error instanceof Error
                                        ? createMutation.error.message
                                        : 'Failed to create internal user'}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="flex gap-2">
                            <Button type="submit" disabled={createMutation.isPending}>
                                {createMutation.isPending ? 'Creating...' : 'Create Internal User'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

export default function CreateInternalUserPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CreateInternalUserContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
